const Storage = require('utils/storage');
const AuthConstants = require('./constants');
const Debug = require('./debug');

const internals = {};

module.exports = {
    migrateAuthPersistence,
    getSavedAuthToken,
    getInitialAuthPersistenceState,
    syncSavedAuthToken,
    handleCrossTabTokenRemoval,
    subscribeLoginContextStorage,
    subscribeSignupContextStorage,
    subscribeMfaContextStorage,
    getPersistedLoginMfaContext,
    getPersistedSignupContext,
    getPersistedSignupStage,
    getBrowserTermsAgreement,
    setBrowserTermsAgreement,
    clearTransientAuthState,
    serializeLoginContext,
    serializeMfaContext,
    serializeSignupContext,
    serializeFields: internals.serializeFields
};

function getSavedAuthToken() {

    migrateAuthPersistence();

    return Storage.get(AuthConstants.token.storageKey) || false;
}

function migrateAuthPersistence() {

    const { currentVersion, keys } = AuthConstants.persistence;
    const persistentResult = internals.runMigrations({
        currentVersion,
        migrations: internals.persistentMigrations,
        storage: Storage,
        versionKey: keys.persistentVersion
    });
    const sessionResult = internals.runMigrations({
        currentVersion,
        migrations: internals.sessionMigrations,
        storage: Storage.session,
        versionKey: keys.sessionVersion
    });

    if (!persistentResult.migrated && !sessionResult.migrated) {
        return false;
    }

    Debug.recordFlow('auth_storage_migrated', {
        persistentFromVersion: persistentResult.fromVersion,
        sessionFromVersion: sessionResult.fromVersion,
        toVersion: currentVersion
    });

    return true;
}

function getInitialAuthPersistenceState() {

    const previousToken = getSavedAuthToken();

    Debug.recordFlow('auth_storage_init', {
        hasToken: !!previousToken
    });

    return {
        previousToken,
        isHandlingCrossTabLogout: false
    };
}

function syncSavedAuthToken({ selectors, store, previousToken }) {

    const state = store.getState();
    const isAuthenticated = selectors.getIsAuthenticated(state);

    if (!isAuthenticated && internals.authIsSettling({ selectors, state })) {

        return {
            previousToken
        };
    }

    if (isAuthenticated) {
        try {
            const apiToken = selectors.getApiToken(state);

            if (apiToken !== previousToken) {
                Storage.set(AuthConstants.token.storageKey, apiToken);
                Debug.recordFlow('token_saved', {
                    hadPreviousToken: !!previousToken,
                    hasNextToken: !!apiToken
                });
            }

            return {
                previousToken: apiToken
            };
        }
        catch (err) {
            Storage.remove(AuthConstants.token.storageKey);
            Debug.warn('token_save_failed', {
                message: err?.message
            });
            return {
                previousToken: false
            };
        }
    }

    Storage.remove(AuthConstants.token.storageKey);

    if (previousToken) {
        Debug.recordFlow('token_removed_for_unauthenticated_state', {});
    }

    return {
        previousToken: false
    };
}

function handleCrossTabTokenRemoval({
    actions,
    authPersistenceState,
    event,
    redux,
    selectors,
    store
}) {

    if (event.key !== AuthConstants.token.storageKey || event.newValue !== null || authPersistenceState.isHandlingCrossTabLogout) {
        return authPersistenceState;
    }

    if (!selectors.getIsAuthenticated(store.getState())) {
        return {
            ...authPersistenceState,
            previousToken: false
        };
    }

    Debug.recordFlow('cross_tab_logout_detected', {});

    const nextAuthPersistenceState = {
        ...authPersistenceState,
        isHandlingCrossTabLogout: true,
        previousToken: false
    };

    redux.dispatch(actions.alerts.notification.push({
        message: 'You were logged out in another tab.'
    }));
    redux.dispatch(actions.auth.logoutSync());

    Storage.remove(AuthConstants.token.storageKey);

    return {
        ...nextAuthPersistenceState,
        isHandlingCrossTabLogout: false
    };
}

function subscribeLoginContextStorage({ store }) {

    Storage.subscribeStorePathWithStore(
        store,
        'auth.loginContext',
        AuthConstants.loginContext.storageKey,
        serializeLoginContext
    );
}

function subscribeSignupContextStorage({ store }) {

    Storage.subscribeStorePathWithStore(
        store,
        'signup.context',
        AuthConstants.signupContext.storageKey,
        serializeSignupContext
    );
}

function subscribeMfaContextStorage({ store }) {

    Storage.session.subscribeStorePathWithStore(
        store,
        'auth.loginContext',
        AuthConstants.mfaSession.loginStorageKey,
        serializeMfaContext
    );
    Storage.session.subscribeStorePathWithStore(
        store,
        'signup.context',
        AuthConstants.mfaSession.signupStorageKey,
        serializeMfaContext
    );
}

function getPersistedLoginMfaContext({ pathname = internals.getCurrentPathname() } = {}) {

    migrateAuthPersistence();

    if (!internals.isLoginMfaContinuationPath(pathname)) {
        Storage.session?.remove?.(AuthConstants.mfaSession.loginStorageKey);
        return {};
    }

    return Storage.session?.get?.(AuthConstants.mfaSession.loginStorageKey) || {};
}

function getPersistedSignupContext({ pathname = internals.getCurrentPathname() } = {}) {

    migrateAuthPersistence();

    const persistedContext = internals.serializeFields({
        fields: AuthConstants.signupContext.persistedFields,
        value: Storage.get(AuthConstants.signupContext.storageKey) || {}
    }) || {};
    const mfaContext = Storage.session?.get?.(AuthConstants.mfaSession.signupStorageKey) || {};

    if (!internals.isSignupMfaContinuationPath(pathname)
        || !internals.hasMatchingMfaOwner({ mfaContext, persistedContext })) {
        Storage.session?.remove?.(AuthConstants.mfaSession.signupStorageKey);
        return persistedContext;
    }

    return {
        ...persistedContext,
        ...mfaContext
    };
}

function getBrowserTermsAgreement() {

    migrateAuthPersistence();

    return Storage.get(AuthConstants.termsAgreement.storageKey) === true;
}

function setBrowserTermsAgreement(agreed = true) {

    if (agreed === true) {
        Storage.set(AuthConstants.termsAgreement.storageKey, true);
        return true;
    }

    Storage.remove(AuthConstants.termsAgreement.storageKey);
    return false;
}

function clearTransientAuthState({ clearTerms = true } = {}) {

    const { sso } = AuthConstants.persistence.keys;

    Storage.remove(AuthConstants.signupContext.storageKey);
    internals.removeStorageKeys(Storage, [
        sso.callbackContext,
        sso.runtimeDebug,
        ...internals.flattenStorageKeys(sso.auth0),
        ...internals.flattenStorageKeys(sso.keycloak)
    ]);
    Storage.session?.remove?.(AuthConstants.mfaSession.loginStorageKey);
    Storage.session?.remove?.(AuthConstants.mfaSession.signupStorageKey);

    if (clearTerms) {
        setBrowserTermsAgreement(false);
    }
}

function getPersistedSignupStage({ stage }) {

    const persistedSignupContext = getPersistedSignupContext();

    if (stage === AuthConstants.stages.identify.slice) {
        const {
            email,
            schoolId
        } = persistedSignupContext;

        return {
            email,
            schoolId
        };
    }

    if (stage === AuthConstants.stages.authenticate.slice) {
        const {
            hasPassword,
            hasSSOToken,
            password,
            verificationToken
        } = persistedSignupContext;

        return {
            hasPassword,
            hasSSOToken,
            password,
            agreeToTerms: getBrowserTermsAgreement(),
            verificationToken
        };
    }

    return {};
}

function serializeLoginContext(loginContext) {

    return internals.serializeFields({
        fields: AuthConstants.loginContext.persistedFields,
        value: loginContext
    });
}

function serializeMfaContext(context) {

    if (!context?.mfaToken) {
        return undefined;
    }

    return internals.serializeFields({
        fields: AuthConstants.mfaSession.fields,
        value: context
    });
}

function serializeSignupContext(signupContext) {

    return internals.serializeFields({
        fields: AuthConstants.signupContext.persistedFields,
        value: signupContext
    });
}

internals.serializeFields = ({ fields, value }) => {

    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const persistedValue = fields.reduce((collector, key) => {

        if (typeof value[key] === 'undefined' || value[key] === null) {
            return collector;
        }

        return {
            ...collector,
            [key]: value[key]
        };
    }, {});

    if (!Object.keys(persistedValue).length) {
        return undefined;
    }

    return persistedValue;
};

internals.authIsSettling = ({ selectors, state }) => {

    if (typeof selectors.getAuthStatus !== 'function') {
        return false;
    }

    const authStatus = selectors.getAuthStatus(state);
    const authStatuses = selectors.AUTH_STATUSES || {};

    return authStatus === authStatuses.INIT
        || authStatus === authStatuses.WAITING;
};

internals.normalizeRememberedEmail = (value) => {

    if (typeof value !== 'string') {
        return null;
    }

    const email = value.trim().toLowerCase();

    return email && email.includes('@') ? email : null;
};

internals.getCurrentPathname = () => typeof window === 'undefined'
    ? null
    : window.location.pathname;

internals.isLoginMfaContinuationPath = (pathname) => !pathname
    || pathname === AuthConstants.authFlow.routes.login.mfa
    || pathname === AuthConstants.authFlow.routes.app.verifyPhone;

internals.isSignupMfaContinuationPath = (pathname) => !pathname
    || pathname === AuthConstants.authFlow.routes.login.mfa
    || pathname.startsWith(`${AuthConstants.authFlow.routes.signup.entry}/`);

internals.hasMatchingMfaOwner = ({ mfaContext = {}, persistedContext = {} } = {}) => {

    const mfaEmail = internals.normalizeRememberedEmail(mfaContext.email);
    const persistedEmail = internals.normalizeRememberedEmail(persistedContext.email);
    const mfaVerificationToken = mfaContext.verificationToken;
    const persistedVerificationToken = persistedContext.verificationToken;
    const hasEitherVerificationToken = !!mfaVerificationToken || !!persistedVerificationToken;
    const verificationTokenMatches = !hasEitherVerificationToken
        || (!!mfaVerificationToken
            && !!persistedVerificationToken
            && mfaVerificationToken === persistedVerificationToken);

    return !!mfaContext.mfaToken
        && !!mfaEmail
        && mfaEmail === persistedEmail
        && verificationTokenMatches;
};

internals.removeStorageKeys = (storage, keys) => {

    keys.filter(Boolean).forEach((key) => storage?.remove?.(key));
};

internals.flattenStorageKeys = (value) => Object.values(value).reduce((keys, entry) => {

    if (entry && typeof entry === 'object') {
        return keys.concat(internals.flattenStorageKeys(entry));
    }

    return keys.concat(entry);
}, []);

internals.migrateDurableAuthPersistence = () => {

    const { keys, legacyKeys } = AuthConstants.persistence;
    const token = Storage.get(keys.token) || Storage.get(legacyKeys.token);
    const currentLoginContext = Storage.get(keys.loginContext) || {};
    const legacyLoginContext = Storage.get(legacyKeys.loginContext) || {};
    const lastLoginEmail = internals.normalizeRememberedEmail(
        currentLoginContext.lastLoginEmail || legacyLoginContext.lastLoginEmail
    );
    const debugEnabled = Storage.getItem?.(keys.debug)
        || Storage.getItem?.(legacyKeys.debug);

    internals.removeStorageKeys(Storage, internals.flattenStorageKeys(legacyKeys));
    internals.removeStorageKeys(Storage, [
        keys.signupContext,
        keys.termsAgreement,
        keys.sso.callbackContext,
        keys.sso.lastSuccessfulEmail,
        keys.sso.runtimeDebug,
        ...internals.flattenStorageKeys(keys.sso.auth0),
        ...internals.flattenStorageKeys(keys.sso.keycloak)
    ]);
    if (token) {
        Storage.set(keys.token, token);
    }

    if (lastLoginEmail) {
        Storage.set(keys.loginContext, { lastLoginEmail });
    }
    else {
        Storage.remove(keys.loginContext);
    }

    if (debugEnabled === 'true') {
        Storage.setItem(keys.debug, 'true');
    }
};

internals.clearLegacyAuthSessionPersistence = () => {

    const { keys, legacyKeys } = AuthConstants.persistence;

    internals.removeStorageKeys(Storage.session, [
        legacyKeys.loginMfaContext,
        legacyKeys.signupMfaContext,
        keys.loginMfaContext,
        keys.signupMfaContext,
        ...internals.flattenStorageKeys(legacyKeys.sso.auth0),
        ...internals.flattenStorageKeys(legacyKeys.sso.keycloak),
        ...internals.flattenStorageKeys(keys.sso.auth0),
        ...internals.flattenStorageKeys(keys.sso.keycloak)
    ]);
};

internals.runMigrations = ({ currentVersion, migrations, storage, versionKey }) => {

    const storedVersion = storage?.get?.(versionKey) || 0;

    if (!storage?.get || !storage?.set || storedVersion >= currentVersion) {
        return {
            migrated: false,
            fromVersion: storedVersion
        };
    }

    migrations
        .filter(({ version }) => version > storedVersion && version <= currentVersion)
        .sort((left, right) => left.version - right.version)
        .forEach(({ migrate }) => migrate());

    storage.set(versionKey, currentVersion);

    return {
        migrated: true,
        fromVersion: storedVersion
    };
};

internals.persistentMigrations = [
    {
        version: 1,
        migrate: internals.migrateDurableAuthPersistence
    },
    {
        version: 2,
        migrate: internals.migrateDurableAuthPersistence
    }
];

internals.sessionMigrations = [
    {
        version: 1,
        migrate: internals.clearLegacyAuthSessionPersistence
    },
    {
        version: 2,
        migrate: internals.clearLegacyAuthSessionPersistence
    }
];
